import { PaginationBar } from "@/shared/ui/PaginationBar";

type TablePaginationLabels = {
  showing: string;
  to: string;
  of: string;
  entries: string;
  previous: string;
  next: string;
};

type TablePaginationProps = {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  labels: TablePaginationLabels;
  className?: string;
};

function buildPageItems(currentPage: number, totalPages: number): (number | "ellipsis")[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const items: (number | "ellipsis")[] = [1];

  if (currentPage > 3) {
    items.push("ellipsis");
  }

  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);

  for (let page = start; page <= end; page += 1) {
    items.push(page);
  }

  if (currentPage < totalPages - 2) {
    items.push("ellipsis");
  }

  items.push(totalPages);
  return items;
}

export function TablePagination({
  currentPage,
  pageSize,
  totalItems,
  onPageChange,
  labels,
  className,
}: TablePaginationProps) {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(Math.max(1, currentPage), totalPages);
  const start = totalItems === 0 ? 0 : (safePage - 1) * pageSize;
  const pageItems = buildPageItems(safePage, totalPages);

  return (
    <PaginationBar
      page={safePage}
      totalPages={totalPages}
      start={start}
      end={Math.min(start + pageSize, totalItems)}
      totalItems={totalItems}
      items={pageItems}
      onPageChange={onPageChange}
      labels={labels}
      className={className}
    />
  );
}
